import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { labs } from "lobbie";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import { ELabsVendorType } from "src/types/global_enums";
import { includesSuperAdminRoleGroup } from "src/utils/roles";
import { match } from "ts-pattern";
import type { RootState } from "../store";

export type LabSettingsStatus = "initial" | "pending" | "failed" | "ready";

export type LabSettingsState = {
    status: LabSettingsStatus;
    vendorType: ELabsVendorType;
    settings?: Partial<labs.ILabSettings>;
    errors?: unknown[];
};

export type FetchLabSettingsArgs = {
    inactive?: boolean;
    vendorType?: ELabsVendorType;
};
export const fetchStaffLabSettings = createAsyncThunk<labs.ILabSettings, FetchLabSettingsArgs>(
    "settings/labs/staff",
    async ({ vendorType = ELabsVendorType.IN_HOUSE, inactive = false }: FetchLabSettingsArgs) => {
        const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/labs/settings?inactive=${inactive}&vendorType=${vendorType}`;
        return (await axios.get<labs.ILabSettings>(route, { withCredentials: true })).data;
    },
);

export const fetchAdminLabSettings = createAsyncThunk<labs.ILabSettings, FetchLabSettingsArgs>(
    "settings/labs/admin",
    async ({ vendorType = ELabsVendorType.IN_HOUSE, inactive = false }, { getState }) => {
        const state = getState() as RootState;
        const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/admin/labs/settings?inactive=${inactive}&vendorType=${vendorType}&accountPublicUuid=${state.account?.account?.publicUuid}`;
        return (await axios.get<labs.ILabSettings>(route, { withCredentials: true })).data;
    },
    {
        // Use of this action requires that the user is a SuperAdmin
        condition: (_, { getState }) => {
            const state = getState() as RootState;
            const isSuperAdmin = includesSuperAdminRoleGroup(state.user.user?.roleGroupIds);
            return isSuperAdmin;
        },
    },
);

export const setLabSettingsVendorType = createAction<ELabsVendorType>("settings/labs/vendor-type");

const initialState: LabSettingsState = {
    status: "initial",
    vendorType: ELabsVendorType.IN_HOUSE,
    settings: undefined,
    errors: undefined,
};

export const LabsSettingSlice = createSlice({
    initialState,
    name: "lab-settings-slice",
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchStaffLabSettings.pending, (state, _) => ({
                ...state,
                status: "pending",
                settings: undefined,
                errors: undefined,
            }))
            .addCase(fetchStaffLabSettings.fulfilled, (state, { payload }) => ({
                ...state,
                status: "ready",
                settings: payload,
                errors: undefined,
            }))
            .addCase(fetchStaffLabSettings.rejected, (state, _) => ({
                ...state,
                status: "failed",
                settings: undefined,
                errors: ["error fetching settings"], // TODO: map error responses to something consumable by UI
            }))
            .addCase(fetchAdminLabSettings.pending, (state, _) => ({
                ...state,
                status: "pending",
                settings: undefined,
                errors: undefined,
            }))
            .addCase(fetchAdminLabSettings.fulfilled, (state, { payload }) => ({
                ...state,
                status: "ready",
                settings: payload,
                errors: undefined,
            }))
            .addCase(fetchAdminLabSettings.rejected, (state, _) => ({
                ...state,
                status: "failed",
                settings: undefined,
                errors: ["error fetching settings"], // TODO: map error responses to something consumable by UI
            }))
            .addCase(setLabSettingsVendorType, (state, { payload }) => ({
                ...state,
                vendorType: payload,
            }));
    },
});

export const currentLabsSettingsStatus = (state: RootState) => state.labSettings.status;
export const currentLabsSettings = (state: RootState) => state.labSettings.settings;
export const currentLabsSettingsErrors = (state: RootState) => state.labSettings.errors;
export const fetchLabSettings = (state: RootState) => {
    return match(includesSuperAdminRoleGroup(state.user.user?.roleGroupIds))
        .with(true, () => fetchAdminLabSettings)
        .otherwise(() => fetchStaffLabSettings);
};
export const currentLabSettingsVendorType = (state: RootState) => state.labSettings.vendorType;
